import { Controller } from "@hotwired/stimulus"

// Controller for managing dynamic lists with add/remove functionality.
// Used for repeatable form fields like accordion items, gallery images, FAQ questions, etc.
//
// Usage:
//   <div data-controller="dynamic-list" data-dynamic-list-template-value="template-id">
//     <div data-dynamic-list-target="item">...</div>
//     <button data-action="click->dynamic-list#addItem">Add</button>
//   </div>
//   <template id="template-id">
//     <div data-dynamic-list-target="item">
//       <input name="items[INDEX][field]">
//       <button data-action="click->dynamic-list#removeItem">Remove</button>
//     </div>
//   </template>
//
// The template should use INDEX as a placeholder for array indices.
// INDEX will be replaced with a unique timestamp-based index on add.
export default class extends Controller {
  static targets = ["item", "container"]
  static values = {
    template: String,  // ID of the template element
    minItems: { type: Number, default: 0 }  // Minimum number of items to keep
  }

  connect() {
    this.updateIndices()
  }

  addItem(event) {
    event.preventDefault()

    const template = document.getElementById(this.templateValue)
    if (!template) {
      console.warn(`Dynamic list: template with id '${this.templateValue}' not found`)
      return
    }

    // Clone the template content
    const clone = template.content.cloneNode(true)

    // Generate a unique index using timestamp
    const uniqueIndex = Date.now()

    // Replace INDEX placeholder in all input names and ids
    clone.querySelectorAll("[name], [id], [for]").forEach((el) => {
      if (el.name) {
        el.name = el.name.replace(/INDEX/g, uniqueIndex)
      }
      if (el.id) {
        el.id = el.id.replace(/INDEX/g, uniqueIndex)
      }
      if (el.htmlFor) {
        el.htmlFor = el.htmlFor.replace(/INDEX/g, uniqueIndex)
      }
    })

    // Find container or use the controller element
    const container = this.hasContainerTarget ? this.containerTarget : this.findItemContainer()

    if (container) {
      container.appendChild(clone)
    } else {
      // Append before the add button
      const addButton = event.currentTarget
      addButton.parentNode.insertBefore(clone, addButton)
    }

    // Focus the first input in the new item
    const newItem = container ? container.lastElementChild : this.itemTargets[this.itemTargets.length - 1]
    const firstInput = newItem?.querySelector("input, textarea, select")
    if (firstInput) {
      firstInput.focus()
    }
  }

  removeItem(event) {
    event.preventDefault()

    // Check minimum items
    if (this.itemTargets.length <= this.minItemsValue) {
      return
    }

    // Find the item element to remove
    const item = event.currentTarget.closest("[data-dynamic-list-target='item']")
    if (item) {
      // Animate out
      item.style.opacity = "0"
      item.style.transform = "translateX(-10px)"
      item.style.transition = "all 0.2s ease-out"

      setTimeout(() => {
        item.remove()
        this.updateIndices()
      }, 200)
    }
  }

  // Find the container that holds item targets
  findItemContainer() {
    if (this.itemTargets.length > 0) {
      return this.itemTargets[0].parentNode
    }
    // Look for common container patterns
    const container = this.element.querySelector("[id$='-container']") ||
                     this.element.querySelector(".items-container") ||
                     this.element.querySelector("[data-dynamic-list-target='container']")
    return container
  }

  // Update array indices to be sequential (optional, for cleaner form data)
  updateIndices() {
    // This is optional - Rails handles non-sequential indices fine
    // But keeping for potential future use
  }
}
